// src/pages/AdminPricingPage.jsx
// Admin Price Management - Pricing Control Plane
// 
// CANONICAL RULE:
// - Prices apply forward only
// - Transactions are immutable
// - History is never rewritten

import React, { useState, useEffect, useCallback } from 'react';
import AdminNav from '../components/AdminNav';
import AdminTopMenuBar from '../components/AdminTopMenuBar';
import AdminMobileBottomNav from '../components/AdminMobileBottomNav';
import UniversalFooter from '../components/UniversalFooter';
import { BACKEND_URL as backendUrl } from '@/config/api';
import { 
  DollarSign, Clock, History, Plus, AlertTriangle,
  CheckCircle, Calendar, Tag, Users, CreditCard,
  Loader2, RefreshCw, Eye, Archive, Lock, Edit, Trash2
} from 'lucide-react';

// backendUrl imported from centralized config as BACKEND_URL

// ============================================================================
// CONFIRMATION MODAL
// ============================================================================
const ConfirmationModal = ({ isOpen, onClose, onConfirm, title, message, pricing, isLoading }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl max-w-lg w-full p-6 border border-slate-700">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-amber-500/20 rounded-lg">
            <AlertTriangle className="w-6 h-6 text-amber-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        
        <p className="text-slate-300 mb-4">{message}</p>
        
        {pricing && (
          <div className="bg-slate-900/50 rounded-lg p-4 mb-4">
            <pre className="text-sm text-slate-400 overflow-auto max-h-40">
              {JSON.stringify(pricing, null, 2)}
            </pre>
          </div>
        )}
        
        <div className="bg-amber-900/20 border border-amber-500/20 rounded-lg p-3 mb-6">
          <p className="text-amber-300 text-sm flex items-center gap-2">
            <Lock className="w-4 h-4" />
            This action cannot be undone. Changes apply only to new transactions.
          </p>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-slate-300 hover:text-white transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Processing...
              </>
            ) : (
              'Confirm Change'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// SUCCESS MODAL
// ============================================================================
const SuccessModal = ({ isOpen, onClose, message }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl max-w-md w-full p-6 border border-slate-700">
        <div className="flex flex-col items-center text-center">
          <div className="p-3 bg-emerald-500/20 rounded-full mb-4">
            <CheckCircle className="w-10 h-10 text-emerald-400" />
          </div>
          <h3 className="text-xl font-semibold text-white mb-2">Success!</h3>
          <p className="text-slate-300 mb-6">{message}</p>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// DELETE CONFIRMATION MODAL
// ============================================================================
const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, itemType, itemName, isLoading, isScheduled }) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-2xl max-w-md w-full p-6 border border-slate-700">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-500/20 rounded-lg">
            <Trash2 className="w-6 h-6 text-red-400" />
          </div>
          <h3 className="text-lg font-semibold text-white">
            {isScheduled ? 'Cancel Scheduled Change?' : `Delete ${itemType}?`}
          </h3>
        </div>
        
        <p className="text-slate-300 mb-4">
          {isScheduled 
            ? <>Are you sure you want to cancel <span className="font-semibold text-white">"{itemName}"</span>?</>
            : <>Are you sure you want to delete <span className="font-semibold text-white">"{itemName}"</span>?</>
          }
        </p>
        
        <div className="bg-red-900/20 border border-red-500/20 rounded-lg p-3 mb-6">
          <p className="text-red-300 text-sm flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {isScheduled 
              ? 'This scheduled change will be cancelled and will not take effect.'
              : `This will remove the ${itemType.toLowerCase()} from active pricing. Existing transactions will not be affected.`
            }
          </p>
        </div>
        
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-slate-300 hover:text-white transition-colors disabled:opacity-50"
          >
            Keep It
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className="px-4 py-2 bg-red-600 hover:bg-red-500 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                {isScheduled ? 'Cancelling...' : 'Deleting...'}
              </>
            ) : (
              isScheduled ? 'Cancel Change' : 'Delete'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// CREATE PRICING FORMS
// ============================================================================
const CreateEngagementPricingForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    peu_base_price: 0.001,
    aeu_base_price: 0.005,
    effective_start: '',
    change_reason: ''
  });
  const [makeChangesNow, setMakeChangesNow] = useState(true);
  
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    setFormData(prev => ({
      ...prev,
      effective_start: tomorrow.toISOString().slice(0, 16)
    }));
  }, []);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    const effectiveDate = makeChangesNow 
      ? new Date().toISOString() 
      : new Date(formData.effective_start).toISOString();
    onSubmit({
      ...formData,
      effective_start: effectiveDate,
      immediate: makeChangesNow
    });
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-slate-400 mb-1">PEU Base Price ($)</label>
          <input
            type="number"
            step="0.0001"
            min="0"
            value={formData.peu_base_price}
            onChange={e => setFormData({...formData, peu_base_price: parseFloat(e.target.value)})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">AEU Base Price ($)</label>
          <input
            type="number"
            step="0.0001"
            min="0"
            value={formData.aeu_base_price}
            onChange={e => setFormData({...formData, aeu_base_price: parseFloat(e.target.value)})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required
          />
        </div>
      </div>
      
      {/* Make Changes Now Toggle */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-sm text-white font-medium">Make Changes Now?</span>
          <div className="flex items-center gap-3">
            <span className={`text-xs ${makeChangesNow ? 'text-slate-500' : 'text-cyan-400'}`}>Schedule</span>
            <button
              type="button"
              onClick={() => setMakeChangesNow(!makeChangesNow)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                makeChangesNow ? 'bg-emerald-500' : 'bg-slate-600'
              }`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                makeChangesNow ? 'left-7' : 'left-1'
              }`} />
            </button>
            <span className={`text-xs ${makeChangesNow ? 'text-emerald-400' : 'text-slate-500'}`}>Now</span>
          </div>
        </label>
      </div>
      
      {!makeChangesNow && (
        <div>
          <label className="block text-sm text-slate-400 mb-1">Effective Date</label>
          <input
            type="datetime-local"
            value={formData.effective_start}
            onChange={e => setFormData({...formData, effective_start: e.target.value})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required={!makeChangesNow}
          />
        </div>
      )}
      
      <div>
        <label className="block text-sm text-slate-400 mb-1">Change Reason</label>
        <textarea
          value={formData.change_reason}
          onChange={e => setFormData({...formData, change_reason: e.target.value})}
          className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
          rows={2}
          minLength={10}
          placeholder="Explain the reason for this pricing change..."
          required
        />
      </div>
      
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-slate-400 hover:text-white text-sm">
          Cancel
        </button>
        <button type="submit" className={`px-4 py-2 text-white rounded-lg text-sm ${
          makeChangesNow ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-violet-600 hover:bg-violet-500'
        }`}>
          {makeChangesNow ? 'Apply Now' : 'Schedule Change'}
        </button>
      </div>
    </form>
  );
};

const CreateSubscriptionForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    tier_name: '',
    tier_code: '',
    monthly_price_usd: 9.99,
    annual_price_usd: null,
    included_reader_credits: 100,
    overage_eligible: true,
    auto_topoff_eligible: false,
    effective_start: '',
    change_reason: '',
    display_order: 0,
    features: []
  });
  const [makeChangesNow, setMakeChangesNow] = useState(true);
  
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    setFormData(prev => ({
      ...prev,
      effective_start: tomorrow.toISOString().slice(0, 16)
    }));
  }, []);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    const effectiveDate = makeChangesNow 
      ? new Date().toISOString() 
      : new Date(formData.effective_start).toISOString();
    onSubmit({
      ...formData,
      effective_start: effectiveDate,
      immediate: makeChangesNow
    });
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-slate-400 mb-1">Tier Name</label>
          <input
            type="text"
            value={formData.tier_name}
            onChange={e => setFormData({...formData, tier_name: e.target.value})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            placeholder="e.g., Pro Reader"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Tier Code</label>
          <input
            type="text"
            value={formData.tier_code}
            onChange={e => setFormData({...formData, tier_code: e.target.value.toLowerCase().replace(/\s/g, '_')})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            placeholder="e.g., pro"
            required
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-slate-400 mb-1">Monthly Price ($)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.monthly_price_usd}
            onChange={e => setFormData({...formData, monthly_price_usd: parseFloat(e.target.value)})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Annual Price ($) <span className="text-slate-500 text-xs">optional</span></label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.annual_price_usd || ''}
            onChange={e => setFormData({...formData, annual_price_usd: e.target.value ? parseFloat(e.target.value) : null})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            placeholder="e.g., 99.99"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm text-slate-400 mb-1">Included Credits</label>
        <input
          type="number"
          min="0"
          value={formData.included_reader_credits}
          onChange={e => setFormData({...formData, included_reader_credits: parseInt(e.target.value)})}
          className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
          required
        />
      </div>
      
      {/* Make Changes Now Toggle */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-sm text-white font-medium">Make Changes Now?</span>
          <div className="flex items-center gap-3">
            <span className={`text-xs ${makeChangesNow ? 'text-slate-500' : 'text-cyan-400'}`}>Schedule</span>
            <button
              type="button"
              onClick={() => setMakeChangesNow(!makeChangesNow)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                makeChangesNow ? 'bg-emerald-500' : 'bg-slate-600'
              }`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                makeChangesNow ? 'left-7' : 'left-1'
              }`} />
            </button>
            <span className={`text-xs ${makeChangesNow ? 'text-emerald-400' : 'text-slate-500'}`}>Now</span>
          </div>
        </label>
      </div>
      
      {!makeChangesNow && (
        <div>
          <label className="block text-sm text-slate-400 mb-1">Effective Date</label>
          <input
            type="datetime-local"
            value={formData.effective_start}
            onChange={e => setFormData({...formData, effective_start: e.target.value})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required={!makeChangesNow}
          />
        </div>
      )}
      
      <div>
        <label className="block text-sm text-slate-400 mb-1">Change Reason</label>
        <textarea
          value={formData.change_reason}
          onChange={e => setFormData({...formData, change_reason: e.target.value})}
          className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
          rows={2}
          minLength={10}
          required
        />
      </div>
      
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-slate-400 hover:text-white text-sm">
          Cancel
        </button>
        <button type="submit" className={`px-4 py-2 text-white rounded-lg text-sm ${
          makeChangesNow ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-cyan-600 hover:bg-cyan-500'
        }`}>
          {makeChangesNow ? 'Create Now' : 'Schedule Tier'}
        </button>
      </div>
    </form>
  );
};

const CreateBundleForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    bundle_name: '',
    bundle_code: '',
    credit_quantity: 100,
    price_usd: 9.99,
    a_la_carte_price: null,
    visibility: 'public',
    effective_start: '',
    change_reason: '',
    badge: ''
  });
  const [makeChangesNow, setMakeChangesNow] = useState(true);
  
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    setFormData(prev => ({
      ...prev,
      effective_start: tomorrow.toISOString().slice(0, 16)
    }));
  }, []);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    const effectiveDate = makeChangesNow 
      ? new Date().toISOString() 
      : new Date(formData.effective_start).toISOString();
    onSubmit({
      ...formData,
      effective_start: effectiveDate,
      immediate: makeChangesNow
    });
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-slate-400 mb-1">Bundle Name</label>
          <input
            type="text"
            value={formData.bundle_name}
            onChange={e => setFormData({...formData, bundle_name: e.target.value})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            placeholder="e.g., Starter Pack"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Bundle Code</label>
          <input
            type="text"
            value={formData.bundle_code}
            onChange={e => setFormData({...formData, bundle_code: e.target.value.toLowerCase().replace(/\s/g, '_')})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            placeholder="e.g., starter"
            required
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-slate-400 mb-1">Credit Quantity</label>
          <input
            type="number"
            min="1"
            value={formData.credit_quantity}
            onChange={e => setFormData({...formData, credit_quantity: parseInt(e.target.value)})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Bundle Price ($)</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={formData.price_usd}
            onChange={e => setFormData({...formData, price_usd: parseFloat(e.target.value)})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm text-slate-400 mb-1">A la carte Price ($) <span className="text-slate-500 text-xs">optional - for non-subscribers</span></label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={formData.a_la_carte_price || ''}
          onChange={e => setFormData({...formData, a_la_carte_price: e.target.value ? parseFloat(e.target.value) : null})}
          className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
          placeholder="e.g., 12.99"
        />
      </div>
      
      {/* Make Changes Now Toggle */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-sm text-white font-medium">Make Changes Now?</span>
          <div className="flex items-center gap-3">
            <span className={`text-xs ${makeChangesNow ? 'text-slate-500' : 'text-cyan-400'}`}>Schedule</span>
            <button
              type="button"
              onClick={() => setMakeChangesNow(!makeChangesNow)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                makeChangesNow ? 'bg-emerald-500' : 'bg-slate-600'
              }`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                makeChangesNow ? 'left-7' : 'left-1'
              }`} />
            </button>
            <span className={`text-xs ${makeChangesNow ? 'text-emerald-400' : 'text-slate-500'}`}>Now</span>
          </div>
        </label>
      </div>
      
      {!makeChangesNow && (
        <div>
          <label className="block text-sm text-slate-400 mb-1">Effective Date</label>
          <input
            type="datetime-local"
            value={formData.effective_start}
            onChange={e => setFormData({...formData, effective_start: e.target.value})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required={!makeChangesNow}
          />
        </div>
      )}
      
      <div>
        <label className="block text-sm text-slate-400 mb-1">Change Reason</label>
        <textarea
          value={formData.change_reason}
          onChange={e => setFormData({...formData, change_reason: e.target.value})}
          className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
          rows={2}
          minLength={10}
          required
        />
      </div>
      
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-slate-400 hover:text-white text-sm">
          Cancel
        </button>
        <button type="submit" className={`px-4 py-2 text-white rounded-lg text-sm ${
          makeChangesNow ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-600 hover:bg-amber-500'
        }`}>
          {makeChangesNow ? 'Create Now' : 'Schedule Bundle'}
        </button>
      </div>
    </form>
  );
};

// ============================================================================
// EDIT FORMS
// ============================================================================
const EditSubscriptionForm = ({ initialData, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    tier_name: initialData?.tier_name || '',
    tier_code: initialData?.tier_code || '',
    monthly_price_usd: initialData?.monthly_price_usd || 9.99,
    annual_price_usd: initialData?.annual_price_usd || null,
    included_reader_credits: initialData?.included_reader_credits || 100,
    overage_eligible: initialData?.overage_eligible ?? true,
    auto_topoff_eligible: initialData?.auto_topoff_eligible ?? false,
    effective_start: '',
    change_reason: '',
    original_id: initialData?.id || initialData?.tier_code
  });
  const [makeChangesNow, setMakeChangesNow] = useState(true);
  
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    setFormData(prev => ({
      ...prev,
      effective_start: tomorrow.toISOString().slice(0, 16)
    }));
  }, []);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    const effectiveDate = makeChangesNow 
      ? new Date().toISOString() 
      : new Date(formData.effective_start).toISOString();
    onSubmit({
      ...formData,
      effective_start: effectiveDate,
      immediate: makeChangesNow
    });
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-slate-400 mb-1">Tier Name</label>
          <input
            type="text"
            value={formData.tier_name}
            onChange={e => setFormData({...formData, tier_name: e.target.value})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Tier Code</label>
          <input
            type="text"
            value={formData.tier_code}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-slate-500 text-sm"
            disabled
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-slate-400 mb-1">Monthly Price ($)</label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.monthly_price_usd}
            onChange={e => setFormData({...formData, monthly_price_usd: parseFloat(e.target.value)})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Annual Price ($) <span className="text-slate-500 text-xs">optional</span></label>
          <input
            type="number"
            step="0.01"
            min="0"
            value={formData.annual_price_usd || ''}
            onChange={e => setFormData({...formData, annual_price_usd: e.target.value ? parseFloat(e.target.value) : null})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            placeholder="e.g., 99.99"
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm text-slate-400 mb-1">Included Credits</label>
        <input
          type="number"
          min="0"
          value={formData.included_reader_credits}
          onChange={e => setFormData({...formData, included_reader_credits: parseInt(e.target.value)})}
          className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
          required
        />
      </div>
      
      {/* Make Changes Now Toggle */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-sm text-white font-medium">Make Changes Now?</span>
          <div className="flex items-center gap-3">
            <span className={`text-xs ${makeChangesNow ? 'text-slate-500' : 'text-cyan-400'}`}>Schedule</span>
            <button
              type="button"
              onClick={() => setMakeChangesNow(!makeChangesNow)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                makeChangesNow ? 'bg-emerald-500' : 'bg-slate-600'
              }`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                makeChangesNow ? 'left-7' : 'left-1'
              }`} />
            </button>
            <span className={`text-xs ${makeChangesNow ? 'text-emerald-400' : 'text-slate-500'}`}>Now</span>
          </div>
        </label>
      </div>
      
      {!makeChangesNow && (
        <div>
          <label className="block text-sm text-slate-400 mb-1">Effective Date</label>
          <input
            type="datetime-local"
            value={formData.effective_start}
            onChange={e => setFormData({...formData, effective_start: e.target.value})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required={!makeChangesNow}
          />
        </div>
      )}
      
      <div>
        <label className="block text-sm text-slate-400 mb-1">Change Reason</label>
        <textarea
          value={formData.change_reason}
          onChange={e => setFormData({...formData, change_reason: e.target.value})}
          className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
          rows={2}
          minLength={10}
          placeholder="Explain reason for this change..."
          required
        />
      </div>
      
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-slate-400 hover:text-white text-sm">
          Cancel
        </button>
        <button type="submit" className={`px-4 py-2 text-white rounded-lg text-sm ${
          makeChangesNow ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-cyan-600 hover:bg-cyan-500'
        }`}>
          {makeChangesNow ? 'Update Now' : 'Schedule Update'}
        </button>
      </div>
    </form>
  );
};

const EditBundleForm = ({ initialData, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    bundle_name: initialData?.bundle_name || '',
    bundle_code: initialData?.bundle_code || '',
    credit_quantity: initialData?.credit_quantity || 100,
    price_usd: initialData?.price_usd || 9.99,
    a_la_carte_price: initialData?.a_la_carte_price || null,
    visibility: initialData?.visibility || 'public',
    effective_start: '',
    change_reason: '',
    original_id: initialData?.id || initialData?.bundle_code
  });
  const [makeChangesNow, setMakeChangesNow] = useState(true);
  
  useEffect(() => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    setFormData(prev => ({
      ...prev,
      effective_start: tomorrow.toISOString().slice(0, 16)
    }));
  }, []);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    const effectiveDate = makeChangesNow 
      ? new Date().toISOString() 
      : new Date(formData.effective_start).toISOString();
    onSubmit({
      ...formData,
      effective_start: effectiveDate,
      immediate: makeChangesNow
    });
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-slate-400 mb-1">Bundle Name</label>
          <input
            type="text"
            value={formData.bundle_name}
            onChange={e => setFormData({...formData, bundle_name: e.target.value})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Bundle Code</label>
          <input
            type="text"
            value={formData.bundle_code}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-slate-500 text-sm"
            disabled
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm text-slate-400 mb-1">Credit Quantity</label>
          <input
            type="number"
            min="1"
            value={formData.credit_quantity}
            onChange={e => setFormData({...formData, credit_quantity: parseInt(e.target.value)})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm text-slate-400 mb-1">Bundle Price ($)</label>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={formData.price_usd}
            onChange={e => setFormData({...formData, price_usd: parseFloat(e.target.value)})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required
          />
        </div>
      </div>
      
      <div>
        <label className="block text-sm text-slate-400 mb-1">A la carte Price ($) <span className="text-slate-500 text-xs">optional - for non-subscribers</span></label>
        <input
          type="number"
          step="0.01"
          min="0"
          value={formData.a_la_carte_price || ''}
          onChange={e => setFormData({...formData, a_la_carte_price: e.target.value ? parseFloat(e.target.value) : null})}
          className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
          placeholder="e.g., 12.99"
        />
      </div>
      
      {/* Make Changes Now Toggle */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-sm text-white font-medium">Make Changes Now?</span>
          <div className="flex items-center gap-3">
            <span className={`text-xs ${makeChangesNow ? 'text-slate-500' : 'text-cyan-400'}`}>Schedule</span>
            <button
              type="button"
              onClick={() => setMakeChangesNow(!makeChangesNow)}
              className={`relative w-12 h-6 rounded-full transition-colors ${
                makeChangesNow ? 'bg-emerald-500' : 'bg-slate-600'
              }`}
            >
              <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                makeChangesNow ? 'left-7' : 'left-1'
              }`} />
            </button>
            <span className={`text-xs ${makeChangesNow ? 'text-emerald-400' : 'text-slate-500'}`}>Now</span>
          </div>
        </label>
      </div>
      
      {!makeChangesNow && (
        <div>
          <label className="block text-sm text-slate-400 mb-1">Effective Date</label>
          <input
            type="datetime-local"
            value={formData.effective_start}
            onChange={e => setFormData({...formData, effective_start: e.target.value})}
            className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
            required={!makeChangesNow}
          />
        </div>
      )}
      
      <div>
        <label className="block text-sm text-slate-400 mb-1">Change Reason</label>
        <textarea
          value={formData.change_reason}
          onChange={e => setFormData({...formData, change_reason: e.target.value})}
          className="w-full bg-slate-900/50 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm"
          rows={2}
          minLength={10}
          placeholder="Explain reason for this change..."
          required
        />
      </div>
      
      <div className="flex justify-end gap-3 pt-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-slate-400 hover:text-white text-sm">
          Cancel
        </button>
        <button type="submit" className={`px-4 py-2 text-white rounded-lg text-sm ${
          makeChangesNow ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-600 hover:bg-amber-500'
        }`}>
          {makeChangesNow ? 'Update Now' : 'Schedule Update'}
        </button>
      </div>
    </form>
  );
};

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================
export default function AdminPricingPage() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPricing, setCurrentPricing] = useState(null);
  const [scheduledPricing, setScheduledPricing] = useState(null);
  const [activeTab, setActiveTab] = useState('current');
  const [showCreateForm, setShowCreateForm] = useState(null);
  const [editItem, setEditItem] = useState(null); // { type: 'subscription'|'bundle', data: {...} }
  const [confirmModal, setConfirmModal] = useState({ open: false, data: null });
  const [historyData, setHistoryData] = useState({ engagement: [], subscriptions: [], bundles: [] });
  const [isProcessing, setIsProcessing] = useState(false);
  const [successModal, setSuccessModal] = useState({ open: false, message: '' });
  const [deleteModal, setDeleteModal] = useState({ open: false, type: null, item: null });
  
  const getAuthToken = () => localStorage.getItem('token') || localStorage.getItem('accessToken');
  
  const isIgnorableError = (err) => {
    const msg = err?.message || String(err);
    return msg.includes('clone') && msg.includes('Response');
  };
  
  const fetchCurrentPricing = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/pricing/current`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (!response.ok) return;
      const text = await response.text();
      const data = text ? JSON.parse(text) : null;
      setCurrentPricing(data);
      setError(null);
    } catch (err) {
      if (!isIgnorableError(err)) setError(err.message);
    }
  }, []);
  
  const fetchScheduledPricing = useCallback(async () => {
    try {
      const response = await fetch(`${backendUrl}/api/admin/pricing/scheduled`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` }
      });
      if (!response.ok) return;
      const text = await response.text();
      const data = text ? JSON.parse(text) : null;
      setScheduledPricing(data);
    } catch (err) {
      console.error('Error fetching scheduled pricing:', err);
    }
  }, []);
  
  const fetchHistory = useCallback(async () => {
    try {
      const token = getAuthToken();
      const headers = { 'Authorization': `Bearer ${token}` };
      
      let engagement = [], subscriptions = [], bundles = [];
      
      try {
        const engRes = await fetch(`${backendUrl}/api/admin/pricing/history/engagement`, { headers });
        if (engRes.ok) {
          const text = await engRes.text();
          engagement = text ? JSON.parse(text) : [];
        }
      } catch (e) {}
      
      try {
        const subRes = await fetch(`${backendUrl}/api/admin/pricing/history/subscriptions`, { headers });
        if (subRes.ok) {
          const text = await subRes.text();
          subscriptions = text ? JSON.parse(text) : [];
        }
      } catch (e) {}
      
      try {
        const bundleRes = await fetch(`${backendUrl}/api/admin/pricing/history/bundles`, { headers });
        if (bundleRes.ok) {
          const text = await bundleRes.text();
          bundles = text ? JSON.parse(text) : [];
        }
      } catch (e) {}
      
      setHistoryData({ engagement, subscriptions, bundles });
    } catch (err) {
      console.error('Error fetching history:', err);
    }
  }, []);
  
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await fetchCurrentPricing();
      await fetchScheduledPricing();
      await fetchHistory();
      setLoading(false);
    };
    loadData();
  }, [fetchCurrentPricing, fetchScheduledPricing, fetchHistory]);
  
  const handleCreateEngagement = async (data) => {
    setConfirmModal({ 
      open: true, 
      type: 'engagement',
      data,
      title: 'Confirm Engagement Pricing Change',
      message: 'This will schedule new engagement unit pricing.'
    });
  };
  
  const handleCreateSubscription = async (data) => {
    setConfirmModal({ 
      open: true, 
      type: 'subscription',
      data,
      title: 'Confirm Subscription Tier Creation',
      message: 'This will schedule a new subscription tier.'
    });
  };
  
  const handleCreateBundle = async (data) => {
    setConfirmModal({ 
      open: true, 
      type: 'bundle',
      data,
      title: 'Confirm Credit Bundle Creation',
      message: 'This will schedule a new credit bundle.'
    });
  };
  
  const handleEditSubscription = async (data) => {
    setConfirmModal({ 
      open: true, 
      type: 'edit_subscription',
      data,
      title: 'Confirm Subscription Tier Update',
      message: 'This will schedule an update to the subscription tier. Current pricing remains for existing subscribers.'
    });
  };
  
  const handleEditBundle = async (data) => {
    setConfirmModal({ 
      open: true, 
      type: 'edit_bundle',
      data,
      title: 'Confirm Credit Bundle Update',
      message: 'This will schedule an update to the credit bundle. Current pricing remains for pending purchases.'
    });
  };
  
  const handleDeleteItem = (type, item) => {
    setDeleteModal({
      open: true,
      type,
      item
    });
  };
  
  const handleDeleteScheduled = (type, item) => {
    // For scheduled items, we use a different naming to indicate cancellation
    const itemName = type === 'engagement' 
      ? `Engagement Pricing (PEU: $${item.peu_base_price}, AEU: $${item.aeu_base_price})`
      : type === 'subscription'
        ? item.tier_name
        : item.bundle_name;
    
    setDeleteModal({
      open: true,
      type: `scheduled_${type}`,
      item: { ...item, displayName: itemName }
    });
  };
  
  const confirmDelete = async () => {
    const { type, item } = deleteModal;
    if (!type || !item) return;
    
    setIsProcessing(true);
    
    try {
      let endpoint;
      let successMessage;
      
      if (type === 'subscription') {
        endpoint = `/api/admin/pricing/subscription/${item.tier_code}`;
        successMessage = `Subscription tier "${item.tier_name}" deleted successfully.`;
      } else if (type === 'bundle') {
        endpoint = `/api/admin/pricing/bundle/${item.bundle_code}`;
        successMessage = `Credit bundle "${item.bundle_name}" deleted successfully.`;
      } else if (type === 'scheduled_engagement') {
        endpoint = `/api/admin/pricing/scheduled/engagement/${item.id}`;
        successMessage = `Scheduled engagement pricing change cancelled.`;
      } else if (type === 'scheduled_subscription') {
        endpoint = `/api/admin/pricing/scheduled/subscription/${item.id}`;
        successMessage = `Scheduled subscription tier "${item.tier_name}" cancelled.`;
      } else if (type === 'scheduled_bundle') {
        endpoint = `/api/admin/pricing/scheduled/bundle/${item.id}`;
        successMessage = `Scheduled credit bundle "${item.bundle_name}" cancelled.`;
      } else {
        return;
      }
      
      const response = await fetch(`${backendUrl}${endpoint}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errText = await response.text();
        let errorMsg = 'Failed to delete';
        try {
          const errData = JSON.parse(errText);
          errorMsg = errData.detail || errorMsg;
        } catch (e) {
          errorMsg = errText || errorMsg;
        }
        throw new Error(errorMsg);
      }
      
      setDeleteModal({ open: false, type: null, item: null });
      setError(null);
      
      await fetchCurrentPricing();
      await fetchScheduledPricing();
      await fetchHistory();
      
      setSuccessModal({ open: true, message: successMessage });
      
    } catch (err) {
      if (!isIgnorableError(err)) setError(err.message);
      setDeleteModal({ open: false, type: null, item: null });
    } finally {
      setIsProcessing(false);
    }
  };
  
  const confirmCreate = async () => {
    const { type, data } = confirmModal;
    if (!type || !data) return;
    
    setIsProcessing(true);
    
    try {
      let endpoint;
      let method = 'POST';
      let successMessage = '';
      
      if (type === 'engagement') {
        endpoint = '/api/admin/pricing/engagement';
        successMessage = data.immediate 
          ? 'Engagement pricing updated successfully! Changes are now active for all new transactions.'
          : 'Engagement pricing scheduled successfully! Changes will take effect on the specified date.';
      }
      else if (type === 'subscription') {
        endpoint = '/api/admin/pricing/subscription';
        successMessage = data.immediate
          ? `Subscription tier "${data.tier_name}" created successfully! Available for new subscribers immediately.`
          : `Subscription tier "${data.tier_name}" scheduled successfully!`;
      }
      else if (type === 'bundle') {
        endpoint = '/api/admin/pricing/bundle';
        successMessage = data.immediate
          ? `Credit bundle "${data.bundle_name}" created successfully! Available for purchase immediately.`
          : `Credit bundle "${data.bundle_name}" scheduled successfully!`;
      }
      else if (type === 'edit_subscription') {
        endpoint = '/api/admin/pricing/subscription';
        successMessage = data.immediate
          ? `Subscription tier "${data.tier_name}" updated successfully! Changes apply to new subscribers only.`
          : `Subscription tier update scheduled successfully!`;
      }
      else if (type === 'edit_bundle') {
        endpoint = '/api/admin/pricing/bundle';
        successMessage = data.immediate
          ? `Credit bundle "${data.bundle_name}" updated successfully! Changes apply to new purchases only.`
          : `Credit bundle update scheduled successfully!`;
      }
      else return;
      
      const response = await fetch(`${backendUrl}${endpoint}`, {
        method,
        headers: {
          'Authorization': `Bearer ${getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });
      
      const responseText = await response.text();
      
      if (!response.ok) {
        let errorMsg = 'Failed to save pricing';
        try {
          const errData = JSON.parse(responseText);
          errorMsg = errData.detail || errorMsg;
        } catch (e) {
          errorMsg = responseText || errorMsg;
        }
        throw new Error(errorMsg);
      }
      
      // Close all modals first
      setConfirmModal({ open: false, data: null, type: null, title: '', message: '' });
      setShowCreateForm(null);
      setEditItem(null);
      setError(null);
      
      // Refresh data
      await fetchCurrentPricing();
      await fetchScheduledPricing();
      await fetchHistory();
      
      // Show success modal
      setSuccessModal({ open: true, message: successMessage });
      
    } catch (err) {
      if (!isIgnorableError(err)) setError(err.message);
      setConfirmModal({ open: false, data: null, type: null, title: '', message: '' });
    } finally {
      setIsProcessing(false);
    }
  };
  
  const refreshAll = () => {
    fetchCurrentPricing();
    fetchScheduledPricing();
    fetchHistory();
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-50">
        <AdminNav />
        <AdminTopMenuBar />
        <div className="flex items-center justify-center py-32">
          <Loader2 className="w-8 h-8 text-violet-400 animate-spin" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-950 text-slate-50">
      <AdminNav />
      <AdminTopMenuBar />
      
      <div className="pb-20 md:pb-0">
        {/* Header */}
        <header className="bg-gradient-to-br from-slate-700 via-slate-800 to-slate-900 border-b border-slate-800">
          <div className="mx-auto max-w-7xl px-4 py-10 sm:py-12">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <DollarSign className="w-8 h-8 text-emerald-400" />
                  <h1 className="text-3xl sm:text-4xl font-bold">Price Management</h1>
                </div>
                <p className="text-sm sm:text-base text-slate-300 max-w-2xl">
                  Configure engagement pricing, subscription tiers, and credit bundles. 
                  All pricing changes are forward-only and auditable.
                </p>
              </div>
              <button
                onClick={refreshAll}
                className="self-start flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 rounded-lg border border-slate-600 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="mx-auto max-w-7xl px-4 py-8">
          {error && (
            <div className="bg-red-900/30 border border-red-500/30 rounded-xl p-4 mb-6">
              <p className="text-red-400">{error}</p>
            </div>
          )}
          
          {/* Tabs */}
          <div className="flex gap-2 mb-6">
            {['current', 'scheduled', 'history'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab
                    ? 'bg-violet-600 text-white'
                    : 'bg-slate-800 text-slate-400 hover:text-white'
                }`}
              >
                {tab === 'current' && <Eye className="w-4 h-4 inline mr-2" />}
                {tab === 'scheduled' && <Calendar className="w-4 h-4 inline mr-2" />}
                {tab === 'history' && <History className="w-4 h-4 inline mr-2" />}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>
          
          {/* Current Pricing Tab */}
          {activeTab === 'current' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Engagement Unit Pricing */}
              <div className="bg-slate-900/60 rounded-2xl border border-slate-800 overflow-hidden">
                <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Tag className="w-5 h-5 text-violet-400" />
                    <h3 className="font-semibold text-white">Engagement Units</h3>
                  </div>
                  <span className={`px-2 py-0.5 text-xs rounded-full ${
                    currentPricing?.engagement_pricing 
                      ? 'bg-emerald-500/20 text-emerald-400' 
                      : 'bg-slate-500/20 text-slate-400'
                  }`}>
                    {currentPricing?.engagement_pricing ? 'Active' : 'Not Set'}
                  </span>
                </div>
                <div className="p-4">
                  {currentPricing?.engagement_pricing ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-slate-800/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">PEU Price</p>
                          <p className="text-lg font-bold text-white">
                            ${currentPricing.engagement_pricing.peu_base_price?.toFixed(4)}
                          </p>
                        </div>
                        <div className="bg-slate-800/50 rounded-lg p-3">
                          <p className="text-xs text-slate-500">AEU Price</p>
                          <p className="text-lg font-bold text-white">
                            ${currentPricing.engagement_pricing.aeu_base_price?.toFixed(4)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No engagement pricing configured</p>
                  )}
                  <button
                    onClick={() => setShowCreateForm('engagement')}
                    className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 bg-violet-600/20 hover:bg-violet-600/30 text-violet-400 rounded-lg text-sm"
                  >
                    <Plus className="w-4 h-4" /> Update Pricing
                  </button>
                </div>
              </div>
              
              {/* Subscription Tiers */}
              <div className="bg-slate-900/60 rounded-2xl border border-slate-800 overflow-hidden">
                <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-cyan-400" />
                    <h3 className="font-semibold text-white">Subscription Tiers</h3>
                  </div>
                  <span className="px-2 py-0.5 text-xs rounded-full bg-slate-500/20 text-slate-400">
                    {currentPricing?.subscription_tiers?.length || 0} tiers
                  </span>
                </div>
                <div className="p-4">
                  {currentPricing?.subscription_tiers?.length > 0 ? (
                    <div className="space-y-2">
                      {currentPricing.subscription_tiers.map((tier, idx) => (
                        <div key={idx} className="bg-slate-800/50 rounded-lg p-3 flex items-center justify-between group">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white">{tier.tier_name}</p>
                            <p className="text-xs text-slate-400">{tier.included_reader_credits} credits</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <p className="text-sm font-bold text-cyan-400">${tier.monthly_price_usd}/mo</p>
                              {tier.annual_price_usd ? (
                                <p className="text-xs text-slate-400">${tier.annual_price_usd}/yr</p>
                              ) : (
                                <p className="text-xs text-slate-500">No annual</p>
                              )}
                            </div>
                            <button
                              onClick={() => setEditItem({ type: 'subscription', data: tier })}
                              className="p-1.5 rounded-lg bg-slate-700/50 hover:bg-cyan-600/30 text-slate-400 hover:text-cyan-400 transition-colors opacity-0 group-hover:opacity-100"
                              title="Edit tier"
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteItem('subscription', tier)}
                              className="p-1.5 rounded-lg bg-slate-700/50 hover:bg-red-600/30 text-slate-400 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                              title="Delete tier"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No subscription tiers configured</p>
                  )}
                  <button
                    onClick={() => setShowCreateForm('subscription')}
                    className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-400 rounded-lg text-sm"
                  >
                    <Plus className="w-4 h-4" /> Add Tier
                  </button>
                </div>
              </div>
              
              {/* Credit Bundles */}
              <div className="bg-slate-900/60 rounded-2xl border border-slate-800 overflow-hidden">
                <div className="p-4 border-b border-slate-800 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-amber-400" />
                    <h3 className="font-semibold text-white">Credit Bundles</h3>
                  </div>
                  <span className="px-2 py-0.5 text-xs rounded-full bg-slate-500/20 text-slate-400">
                    {currentPricing?.credit_bundles?.length || 0} bundles
                  </span>
                </div>
                <div className="p-4">
                  {currentPricing?.credit_bundles?.length > 0 ? (
                    <div className="space-y-2">
                      {currentPricing.credit_bundles.map((bundle, idx) => (
                        <div key={idx} className="bg-slate-800/50 rounded-lg p-3 flex items-center justify-between group">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-white">{bundle.bundle_name}</p>
                            <p className="text-xs text-slate-400">{bundle.credit_quantity} credits</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right">
                              <p className="text-xs text-slate-400">Subscriber: <span className="text-amber-400 font-semibold">${bundle.price_usd}</span></p>
                              {bundle.a_la_carte_price ? (
                                <p className="text-xs text-slate-400">A la carte: <span className="text-amber-300 font-semibold">${bundle.a_la_carte_price}</span></p>
                              ) : (
                                <p className="text-xs text-slate-500">No a la carte</p>
                              )}
                            </div>
                            <button
                              onClick={() => setEditItem({ type: 'bundle', data: bundle })}
                              className="p-1.5 rounded-lg bg-slate-700/50 hover:bg-amber-600/30 text-slate-400 hover:text-amber-400 transition-colors opacity-0 group-hover:opacity-100"
                              title="Edit bundle"
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteItem('bundle', bundle)}
                              className="p-1.5 rounded-lg bg-slate-700/50 hover:bg-red-600/30 text-slate-400 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                              title="Delete bundle"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-500 text-sm">No credit bundles configured</p>
                  )}
                  <button
                    onClick={() => setShowCreateForm('bundle')}
                    className="mt-4 w-full flex items-center justify-center gap-2 px-3 py-2 bg-amber-600/20 hover:bg-amber-600/30 text-amber-400 rounded-lg text-sm"
                  >
                    <Plus className="w-4 h-4" /> Add Bundle
                  </button>
                </div>
              </div>
            </div>
          )}
          
          {/* Scheduled Tab */}
          {activeTab === 'scheduled' && (
            <div className="bg-slate-900/60 rounded-2xl border border-slate-800 p-6">
              {scheduledPricing?.engagement_pricing?.length > 0 || 
               scheduledPricing?.subscription_tiers?.length > 0 || 
               scheduledPricing?.credit_bundles?.length > 0 ? (
                <div className="space-y-4">
                  {scheduledPricing.engagement_pricing?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-400 mb-2">Scheduled Engagement Pricing</h3>
                      {scheduledPricing.engagement_pricing.map((p, idx) => (
                        <div key={idx} className="bg-slate-800/50 rounded-lg p-3 mb-2 flex justify-between items-center group">
                          <div>
                            <p className="text-white text-sm">PEU: ${p.peu_base_price} | AEU: ${p.aeu_base_price}</p>
                            <p className="text-xs text-slate-400">Effective: {new Date(p.effective_start).toLocaleString()}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">Scheduled</span>
                            <button
                              onClick={() => handleDeleteScheduled('engagement', p)}
                              className="p-1.5 rounded-lg bg-slate-700/50 hover:bg-red-600/30 text-slate-400 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                              title="Cancel scheduled change"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {scheduledPricing.subscription_tiers?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-400 mb-2">Scheduled Subscription Tiers</h3>
                      {scheduledPricing.subscription_tiers.map((t, idx) => (
                        <div key={idx} className="bg-slate-800/50 rounded-lg p-3 mb-2 flex justify-between items-center group">
                          <div>
                            <p className="text-white text-sm">{t.tier_name} - ${t.monthly_price_usd}/mo</p>
                            <p className="text-xs text-slate-400">Effective: {new Date(t.effective_start).toLocaleString()}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">Scheduled</span>
                            <button
                              onClick={() => handleDeleteScheduled('subscription', t)}
                              className="p-1.5 rounded-lg bg-slate-700/50 hover:bg-red-600/30 text-slate-400 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                              title="Cancel scheduled change"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {scheduledPricing.credit_bundles?.length > 0 && (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-400 mb-2">Scheduled Credit Bundles</h3>
                      {scheduledPricing.credit_bundles.map((b, idx) => (
                        <div key={idx} className="bg-slate-800/50 rounded-lg p-3 mb-2 flex justify-between items-center group">
                          <div>
                            <p className="text-white text-sm">{b.bundle_name} - ${b.price_usd} ({b.credit_quantity} credits)</p>
                            <p className="text-xs text-slate-400">Effective: {new Date(b.effective_start).toLocaleString()}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs rounded-full">Scheduled</span>
                            <button
                              onClick={() => handleDeleteScheduled('bundle', b)}
                              className="p-1.5 rounded-lg bg-slate-700/50 hover:bg-red-600/30 text-slate-400 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100"
                              title="Cancel scheduled change"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12 text-slate-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No pricing changes scheduled</p>
                </div>
              )}
            </div>
          )}
          
          {/* History Tab */}
          {activeTab === 'history' && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-slate-900/60 rounded-2xl border border-slate-800 p-4">
                <h3 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" /> Engagement History
                </h3>
                {historyData.engagement?.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {historyData.engagement.map((p, idx) => (
                      <div key={idx} className="bg-slate-800/50 rounded-lg p-3 flex justify-between items-center">
                        <div>
                          <p className="text-sm text-white">PEU: ${p.peu_base_price} | AEU: ${p.aeu_base_price}</p>
                          <p className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString()}</p>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          p.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'
                        }`}>{p.status}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No history available</p>
                )}
              </div>
              
              <div className="bg-slate-900/60 rounded-2xl border border-slate-800 p-4">
                <h3 className="text-sm font-semibold text-slate-400 mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" /> Subscription History
                </h3>
                {historyData.subscriptions?.length > 0 ? (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {historyData.subscriptions.map((t, idx) => (
                      <div key={idx} className="bg-slate-800/50 rounded-lg p-3 flex justify-between items-center">
                        <div>
                          <p className="text-sm text-white">{t.tier_name} - ${t.monthly_price_usd}/mo</p>
                          <p className="text-xs text-slate-500">{new Date(t.created_at).toLocaleDateString()}</p>
                        </div>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${
                          t.status === 'active' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-slate-500/20 text-slate-400'
                        }`}>{t.status}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm">No history available</p>
                )}
              </div>
            </div>
          )}
        </main>
        
        {/* Create Form Modal */}
        {showCreateForm && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-2xl max-w-md w-full p-6 border border-slate-700 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-white mb-4">
                {showCreateForm === 'engagement' && 'Schedule Engagement Pricing'}
                {showCreateForm === 'subscription' && 'Create Subscription Tier'}
                {showCreateForm === 'bundle' && 'Create Credit Bundle'}
              </h3>
              
              {showCreateForm === 'engagement' && (
                <CreateEngagementPricingForm 
                  onSubmit={handleCreateEngagement}
                  onCancel={() => setShowCreateForm(null)}
                />
              )}
              {showCreateForm === 'subscription' && (
                <CreateSubscriptionForm 
                  onSubmit={handleCreateSubscription}
                  onCancel={() => setShowCreateForm(null)}
                />
              )}
              {showCreateForm === 'bundle' && (
                <CreateBundleForm 
                  onSubmit={handleCreateBundle}
                  onCancel={() => setShowCreateForm(null)}
                />
              )}
            </div>
          </div>
        )}
        
        {/* Edit Item Modal */}
        {editItem && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-slate-800 rounded-2xl max-w-md w-full p-6 border border-slate-700 max-h-[90vh] overflow-y-auto">
              <h3 className="text-lg font-semibold text-white mb-4">
                {editItem.type === 'subscription' ? 'Edit Subscription Tier' : 'Edit Credit Bundle'}
              </h3>
              
              {/* Warning Banner in Edit Modal */}
              <div className="bg-amber-900/30 border border-amber-500/30 rounded-lg p-3 mb-4">
                <p className="text-amber-300 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Changes create a new version. Current pricing remains for existing transactions.
                </p>
              </div>
              
              {editItem.type === 'subscription' && (
                <EditSubscriptionForm 
                  initialData={editItem.data}
                  onSubmit={(data) => handleEditSubscription(data)}
                  onCancel={() => setEditItem(null)}
                />
              )}
              {editItem.type === 'bundle' && (
                <EditBundleForm 
                  initialData={editItem.data}
                  onSubmit={(data) => handleEditBundle(data)}
                  onCancel={() => setEditItem(null)}
                />
              )}
            </div>
          </div>
        )}
        
        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={confirmModal.open}
          onClose={() => setConfirmModal({ open: false, data: null })}
          onConfirm={confirmCreate}
          title={confirmModal.title}
          message={confirmModal.message}
          pricing={confirmModal.data}
          isLoading={isProcessing}
        />
        
        {/* Success Modal */}
        <SuccessModal
          isOpen={successModal.open}
          onClose={() => setSuccessModal({ open: false, message: '' })}
          message={successModal.message}
        />
        
        {/* Delete Confirmation Modal */}
        <DeleteConfirmationModal
          isOpen={deleteModal.open}
          onClose={() => setDeleteModal({ open: false, type: null, item: null })}
          onConfirm={confirmDelete}
          itemType={
            deleteModal.type?.startsWith('scheduled_') 
              ? 'Scheduled Change'
              : deleteModal.type === 'subscription' 
                ? 'Subscription Tier' 
                : 'Credit Bundle'
          }
          itemName={deleteModal.item?.displayName || deleteModal.item?.tier_name || deleteModal.item?.bundle_name || ''}
          isLoading={isProcessing}
          isScheduled={deleteModal.type?.startsWith('scheduled_')}
        />
      </div>
      
      <AdminMobileBottomNav />
      <UniversalFooter />
    </div>
  );
}
